import { BoxGeometry, ConeGeometry, Group, IcosahedronGeometry, Mesh, MeshLambertMaterial, type BufferGeometry, type Material } from 'three';

/**
 * EN-23 Gaviota (GDD §8.3, issue #106) in low-poly primitives with flat shading: a white port seagull with grey
 * wings and a yellow beak, about the size of the gallinazo (hitbox circle of 0.4 u, 70-80 % of the model).
 * Geometries and materials are created once per scene and shared by every seagull of every flock.
 */
export interface Seagull {
  group: Group;
  wings: [Mesh, Mesh];
}

export const SEAGULL_COLORS = { body: 0xeef0f2, wing: 0x8c939b, beak: 0xe0b83c } as const;

export function createSeagullFactory(): { build(name: string): Seagull; dispose(): void } {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const own = <T extends BufferGeometry>(geometry: T): T => {
    geometries.push(geometry);
    return geometry;
  };
  const paint = (color: number) => {
    const material = new MeshLambertMaterial({ color, flatShading: true });
    materials.push(material);
    return material;
  };
  const bodyGeometry = own(new IcosahedronGeometry(0.22, 0));
  const headGeometry = own(new IcosahedronGeometry(0.1, 0));
  const beakGeometry = own(new ConeGeometry(0.04, 0.12, 4));
  const tailGeometry = own(new ConeGeometry(0.08, 0.26, 4));
  const wingGeometry = own(new BoxGeometry(0.36, 0.04, 0.46));
  const white = paint(SEAGULL_COLORS.body);
  const grey = paint(SEAGULL_COLORS.wing);
  const yellow = paint(SEAGULL_COLORS.beak);

  return {
    build(name) {
      const group = new Group();
      group.name = name;
      const body = new Mesh(bodyGeometry, white);
      body.scale.set(1.3, 0.8, 0.8);
      const head = new Mesh(headGeometry, white);
      head.position.set(-0.3, 0.06, 0);
      const beak = new Mesh(beakGeometry, yellow);
      beak.rotation.z = Math.PI / 2;
      beak.position.set(-0.43, 0.04, 0);
      const tail = new Mesh(tailGeometry, white);
      tail.rotation.z = -Math.PI / 2;
      tail.position.set(0.38, 0, 0);
      const left = new Mesh(wingGeometry, grey);
      left.position.set(0.02, 0.05, 0.3);
      const right = new Mesh(wingGeometry, grey);
      right.position.set(0.02, 0.05, -0.3);
      group.add(body, head, beak, tail, left, right);
      return { group, wings: [left, right] };
    },
    dispose() {
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
